import { deriveGroupRounds, orderGroupItems } from "./groupRounds";
import {
  effectiveExecutionProfile,
  prescribedExerciseForItem,
} from "./effectiveExecutionProfile";
import { isSetPerformed } from "./executionUi";
import { resolveWorkoutPreferences } from "./workoutPreferenceDefaults";

// §14.2: there is no timer maximum beyond 3,600 s.
export const REST_TIMER_MAX_SECONDS = 3600;
export const REST_TIMER_SOURCES = Object.freeze(["AUTO", "MANUAL"]);

export function startRestTimer({
  now = Date.now(),
  durationSeconds,
  anchor,
  source = "MANUAL",
} = {}) {
  if (
    !Number.isInteger(durationSeconds) ||
    durationSeconds < 0 ||
    durationSeconds > REST_TIMER_MAX_SECONDS
  ) {
    throw new TypeError("durationSeconds must be an integer from 0 through 3600");
  }
  const startedAt = new Date(now);
  if (Number.isNaN(startedAt.getTime())) throw new TypeError("now must be valid");
  return {
    startedAt: startedAt.toISOString(),
    endsAt: new Date(startedAt.getTime() + durationSeconds * 1000).toISOString(),
    durationSeconds,
    anchor: anchor || null,
    source,
  };
}

// +30 s never takes the timer beyond REST_TIMER_MAX_SECONDS, and endsAt stays
// startedAt + durationSeconds (§14.3, §14.5).
export function extendRestTimer(timer, seconds = 30) {
  if (!timer) return null;
  const durationSeconds = Math.min(REST_TIMER_MAX_SECONDS, timer.durationSeconds + seconds);
  return {
    ...timer,
    endsAt: new Date(new Date(timer.startedAt).getTime() + durationSeconds * 1000).toISOString(),
    durationSeconds,
  };
}

export function remainingRestSeconds(timer, now = Date.now()) {
  if (!timer) return 0;
  return Math.max(0, Math.ceil((new Date(timer.endsAt).getTime() - new Date(now).getTime()) / 1000));
}

export function dismissRestTimer() {
  return null;
}

export function resolveRestDuration(prescribedRestSeconds, defaultRestSeconds) {
  return prescribedRestSeconds ?? defaultRestSeconds;
}

// §14.5 normalization. Malformed input is data, never an exception: a finite,
// non-negative number rounds to the nearest whole second and is capped at
// REST_TIMER_MAX_SECONDS; anything else is unusable (null). 0 stays 0.
export function normalizeRestSeconds(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  return Math.min(REST_TIMER_MAX_SECONDS, Math.round(value));
}

// §14.1 anchors are pure decisions over two consecutive local records. Phase 6
// owns the timer integration; nothing here dispatches or mutates.

// SINGLE/TIMED_HOLD (and each GROUP lane set) anchor only on the first
// historical performed transition. `performedAt` keeps its first value when a
// required field is cleared (§12.6), so clear -> re-enter is never a new anchor
// even though the current boolean `performed` state flips back to true.
export function isFirstPerformedTransition(previousSet, nextSet) {
  return (previousSet?.performedAt ?? null) == null && nextSet?.performedAt != null;
}

function blockGrouping(record, blockKey) {
  const block = (record?.server?.snapshot?.blocks || [])
    .find((candidate) => candidate.blockKey === blockKey);
  return block?.grouping === "GROUP" ? "GROUP" : "SINGLE";
}

function isCardio(record, item) {
  return effectiveExecutionProfile(record, item)?.measurementType === "CARDIO_MINUTES";
}

// The canonical execution order is the order the execution screen presents:
// snapshot blocks in order (a GROUP block round by round, lanes in lane order
// within a round; other blocks item by item, sets by setIndex), then items that
// no snapshot block represents (added items) in document order. It only orders
// candidate transitions; it never decides whether work remains.
export function canonicalSetSequence(record) {
  const documentItems = record?.document?.items || [];
  const bySetIndex = (sets = []) => sets
    .filter((set) => Number.isInteger(set.setIndex))
    .slice()
    .sort((left, right) => left.setIndex - right.setIndex);
  const itemSets = (item) => bySetIndex(item.sets).map((set) => ({ item, set }));
  const represented = new Set();
  const sequence = [];

  (record?.server?.snapshot?.blocks || []).forEach((block) => {
    const blockItems = documentItems.filter((item) => item.blockKey === block.blockKey);
    blockItems.forEach((item) => represented.add(item.itemId));
    if (block.grouping !== "GROUP") {
      blockItems.forEach((item) => sequence.push(...itemSets(item)));
      return;
    }
    const lanes = orderGroupItems(blockItems);
    const indexes = Array.from(new Set(lanes.flatMap((item) =>
      bySetIndex(item.sets).map((set) => set.setIndex)
    ))).sort((left, right) => left - right);
    indexes.forEach((setIndex) => {
      lanes.forEach((item) => {
        bySetIndex(item.sets)
          .filter((set) => set.setIndex === setIndex)
          .forEach((set) => sequence.push({ item, set }));
      });
    });
  });
  documentItems
    .filter((item) => !represented.has(item.itemId))
    .forEach((item) => sequence.push(...itemSets(item)));
  return sequence;
}

// Remaining work = any set anywhere in the execution, other than the anchor
// itself, that still exists, belongs to a non-skipped, non-Cardio item/lane,
// and is not performed under the canonical performed rules. Display order is
// irrelevant: the athlete can work out of order. Skipped items/lanes and
// Cardio never count (§14.1 final rest).
export function hasRemainingActionableWork(record, { itemId, setId } = {}) {
  const rirEnabled = record?.server?.policy?.rirInputEnabled !== false;
  return (record?.document?.items || []).some((item) => {
    if (item.skipped || isCardio(record, item)) return false;
    const profile = effectiveExecutionProfile(record, item);
    return (item.sets || []).some((set) =>
      !(item.itemId === itemId && set.setId === setId) &&
      !isSetPerformed(profile, set, rirEnabled)
    );
  });
}

function groupRound(record, blockKey, setIndex) {
  const items = (record?.document?.items || []).filter((item) => item.blockKey === blockKey);
  return deriveGroupRounds(record, items).find((round) => round.setIndex === setIndex) || null;
}

// Returns the §14.3 anchor produced by previousRecord -> nextRecord, or null.
// Only a set's first performed transition can anchor. Skip/Unskip, edits of
// already-performed sets and re-entry after a clear never do. A GROUP set
// anchors only when that transition completes its actionable round.
export function resolveRestAnchor(previousRecord, nextRecord) {
  if (nextRecord?.document?.status !== "ACTIVE") return null;
  const previousSets = new Map();
  (previousRecord?.document?.items || []).forEach((item) =>
    (item.sets || []).forEach((set) => previousSets.set(`${item.itemId}:${set.setId}`, set))
  );
  const rirEnabled = nextRecord?.server?.policy?.rirInputEnabled !== false;
  let anchor = null;

  canonicalSetSequence(nextRecord).forEach(({ item, set }) => {
    if (item.skipped || isCardio(nextRecord, item)) return;
    if (!isFirstPerformedTransition(previousSets.get(`${item.itemId}:${set.setId}`), set)) return;
    if (!isSetPerformed(effectiveExecutionProfile(nextRecord, item), set, rirEnabled)) return;
    const grouping = blockGrouping(nextRecord, item.blockKey);
    if (grouping === "GROUP") {
      const before = groupRound(previousRecord, item.blockKey, set.setIndex);
      const after = groupRound(nextRecord, item.blockKey, set.setIndex);
      if (before?.complete || !after?.complete) return;
    }
    if (!hasRemainingActionableWork(nextRecord, { itemId: item.itemId, setId: set.setId })) return;
    anchor = {
      itemId: item.itemId,
      setId: set.setId,
      round: grouping === "GROUP" ? set.setIndex : null,
    };
  });
  return anchor;
}

// AUTOMATIC starts at an anchor; MANUAL offers Play at the same anchor; OFF
// never shows a timer (§14.1).
export function shouldStartForAnchor({ mode, anchor } = {}) {
  return mode === "AUTOMATIC" && anchor != null;
}

function anchorItem(record, anchor) {
  return (record?.document?.items || []).find((item) => item.itemId === anchor?.itemId) || null;
}

function anchorSet(record, anchor) {
  return (anchorItem(record, anchor)?.sets || []).find((set) => set.setId === anchor?.setId) || null;
}

// The raw prescription for an anchor (§14.5), before normalization:
// - GROUP: the block's round rest, for every set of the group, including an
//   ADDED set inside it (group rest is prescribed per round, not per set);
// - SINGLE/TIMED_HOLD: the matching prescribed set's rest. An ADDED set has no
//   prescribedSetKey and an ADDED exercise no prescribed exercise, so neither
//   has a prescription. No sibling set or exercise is ever borrowed.
export function prescribedRestSecondsForAnchor(record, anchor) {
  const item = anchorItem(record, anchor);
  if (!item) return null;
  if (blockGrouping(record, item.blockKey) === "GROUP") {
    const block = (record?.server?.snapshot?.blocks || [])
      .find((candidate) => candidate.blockKey === item.blockKey);
    return block?.prescribedRoundRestSeconds ?? null;
  }
  const set = anchorSet(record, anchor);
  if (!set?.prescribedSetKey) return null;
  const prescribed = (prescribedExerciseForItem(record, item)?.sets || [])
    .find((candidate) => candidate.setKey === set.prescribedSetKey);
  return prescribed?.prescribedRestSeconds ?? null;
}

// prescription ?? default (§14.2), each normalized first. An explicit
// prescribed 0 is kept (it means "no rest"); an unusable prescription falls
// back to a usable default; null when neither is usable.
export function resolveAnchorRestSeconds(record, anchor, defaultRestSeconds) {
  const prescribed = prescribedRestSecondsForAnchor(record, anchor);
  return resolveRestDuration(
    prescribed == null ? null : normalizeRestSeconds(prescribed),
    normalizeRestSeconds(defaultRestSeconds)
  ) ?? null;
}

// The rest consequence of one athlete transition (§14.5), folded into the
// record that transition produced: the returned record keeps nextRecord's
// revision, timing and activity. A new valid anchor first replaces any timer
// and offer; the mode and default current at that anchor then decide what
// exists. A transition without an anchor returns nextRecord itself. It never
// throws: an unexpected rest error leaves the athlete's record unchanged.
export function applyRestAnchor(previousRecord, nextRecord, preferences) {
  try {
    const anchor = resolveRestAnchor(previousRecord, nextRecord);
    if (!anchor) return nextRecord;
    const { restTimerMode: mode, defaultRestSeconds } = resolveWorkoutPreferences(preferences);
    const replaced = { ...nextRecord, restTimer: null, restOffer: null };
    if (mode === "OFF") return replaced;
    const durationSeconds = resolveAnchorRestSeconds(nextRecord, anchor, defaultRestSeconds);
    if (!durationSeconds) return replaced;
    // The anchor event is the set's first performedAt, i.e. the commit time.
    const anchoredAt = new Date(anchorSet(nextRecord, anchor).performedAt).toISOString();
    if (shouldStartForAnchor({ mode, anchor })) {
      return {
        ...replaced,
        restTimer: startRestTimer({ now: anchoredAt, durationSeconds, anchor, source: "AUTO" }),
      };
    }
    return { ...replaced, restOffer: { anchor, durationSeconds, offeredAt: anchoredAt } };
  } catch (_error) {
    return nextRecord;
  }
}
